import { EditOutlined, LeftOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Card, Flex, Table } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { MenuProps } from 'antd/lib'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  useStocksServiceDeleteProductEndpoint,
  useStocksServiceGetMedicineListEndpoint,
  useStocksServiceGetMedicineListEndpointKey
} from 'src/api/queries'
import { ApiError, GetProductListResponseItem } from 'src/api/requests'
import {
  DeleteConfirm,
  message,
  SearchBar,
  TableActionButtons
} from 'src/components'
import { useTablePagination } from 'src/hooks'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { formErrorHandler, resetPagination } from 'src/utils'

export const ProductsCard = () => {
  const { t } = useLocales()
  const pagination = useTablePagination()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [search, setSearch] = useState('')

  const queryParams = {
    pageNumber: pagination.current,
    pageSize: pagination.pageSize,
    ...(search && {
      searchTerm: search
    })
  }

  const { data, isLoading } =
    useStocksServiceGetMedicineListEndpoint(queryParams)

  const { mutate } = useStocksServiceDeleteProductEndpoint({
    onError: (e: ApiError) => formErrorHandler(e.body.errors),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [useStocksServiceGetMedicineListEndpointKey]
      })
      message.success(t('medicine_register.products.delete_success'))
    }
  })

  const onSearch = (val: string) => {
    setSearch(val)
    resetPagination(pagination)
  }

  const getActionItems = useMemo(
    () =>
      (id: string): MenuProps['items'] => {
        return [
          {
            key: 'edit',
            label: t('common.edit'),
            icon: <EditOutlined />,
            onClick: () => navigate(paths.medicinesRegister.products.edit(id))
          },
          {
            key: 'divider',
            type: 'divider'
          },
          {
            key: 'delete',
            label: (
              <DeleteConfirm
                title={t('medicine_register.products.delete')}
                onConfirm={() => mutate({ id })}
              />
            ),
            danger: true
          }
        ]
      },
    [t]
  )

  const columns: ColumnsType<GetProductListResponseItem> = [
    {
      key: 'name',
      title: t('medicine_register.products.title'),
      render: (_, { commercialName, doses }) => (
        <>
          {commercialName} {doses}
        </>
      )
    },
    {
      key: 'actions',
      width: 64,
      render: ({ id }) => (
        <TableActionButtons items={getActionItems(id)} justify="center" />
      )
    }
  ]

  return (
    <Card>
      <Flex vertical gap={16}>
        <Flex gap={16}>
          <Button
            icon={<LeftOutlined />}
            onClick={() => navigate(paths.medicinesRegister.root)}
          >
            {t('common.back')}
          </Button>

          <SearchBar className="flex-1" onSearch={onSearch} />

          <Button
            type="primary"
            onClick={() => navigate(paths.medicinesRegister.products.add)}
          >
            {t('common.add')}
          </Button>
        </Flex>

        <Table
          rowKey="id"
          loading={isLoading}
          columns={columns}
          dataSource={data?.data}
          scroll={{ x: 'max-content' }}
          bordered
          pagination={{ ...pagination, total: data?.totalCount }}
        />
      </Flex>
    </Card>
  )
}
